import React, { Fragment, useState, useEffect } from 'react';
import { Row, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';
import { rankItem } from '@tanstack/match-sorter-utils';

// فیلتر سراسری با تأخیر (Debounced Input)
const DebouncedInput = ({ value: initialValue, onChange, debounce = 500, ...props }) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <div className="search-box me-2 mb-2 d-inline-block col-12">
      <input
        {...props}
        value={value}
        className="form-control search"
        onChange={(e) => setValue(e.target.value)}
      />
      <i className="bx bx-search-alt search-icon"></i>
    </div>
  );
};

const TableContainer = ({
  columns,
  data,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  SearchPlaceholder = 'جستجو در جدول...',
  showPagination = true
}) => {
  const [globalFilter, setGlobalFilter] = useState('');

  // فیلتر فازی برای جستجوی سراسری
  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  };

  // تنظیم جدول
  const table = useReactTable({
    columns,
    data,
    filterFns: { fuzzy: fuzzyFilter },
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    setPageSize,
    getState
  } = table;

  // تنظیم اندازه صفحه
  useEffect(() => {
    if (customPageSize) {
      setPageSize(customPageSize);
    }
  }, [customPageSize, setPageSize]);

  // محاسبه تعداد ردیف‌های نمایش‌داده‌شده
  const displayedRows = Math.min(getState().pagination.pageSize, getRowModel().rows.length);

  return (
    <Fragment>
      {showPagination && (
        <Row className="mb-3">
          <div className="border border-dashed border-end-0 border-start-0 p-3">
            <Row>
              <div className="col-sm-5">
                <DebouncedInput
                  value={globalFilter ?? ''}
                  onChange={(value) => setGlobalFilter(String(value))}
                  placeholder={SearchPlaceholder}
                />
              </div>
            </Row>
          </div>
        </Row>
      )}

      <div className={divClass} dir="rtl">
        <Table hover className={tableClass}>
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup) => (
              <tr className={trClass} key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={thClass}
                    {...{
                      onClick: header.column.getToggleSortingHandler()
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                    {{
                      asc: ' 🔼',
                      desc: ' 🔽'
                    }[header.column.getIsSorted()] ?? null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {getRowModel().rows.length > 0 ? (
              getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center text-muted">
                  هیچ داده‌ای یافت نشد
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {showPagination && (
        <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
          <div className="col-sm">
            <div className="text-muted">
              نمایش{' '}
              <span className="fw-semibold">
                {displayedRows.toLocaleString('fa-IR')}
              </span>{' '}
              از{' '}
              <span className="fw-semibold">
                {data.length.toLocaleString('fa-IR')}
              </span>{' '}
              نتیجه
            </div>
          </div>
          <div className="col-sm-auto">
            <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
              <li className={getCanPreviousPage() ? 'page-item' : 'page-item disabled'}>
                <Link to="#" className="page-link" onClick={previousPage}>
                  قبلی
                </Link>
              </li>
              {getPageOptions().map((item, key) => (
                <li className="page-item" key={key}>
                  <Link
                    to="#"
                    className={
                      getState().pagination.pageIndex === item
                        ? 'page-link active'
                        : 'page-link'
                    }
                    onClick={() => setPageIndex(item)}
                  >
                    {(item + 1).toLocaleString('fa-IR')}
                  </Link>
                </li>
              ))}
              <li className={getCanNextPage() ? 'page-item' : 'page-item disabled'}>
                <Link to="#" className="page-link" onClick={nextPage}>
                  بعدی
                </Link>
              </li>
            </ul>
          </div>
        </Row>
      )}
    </Fragment>
  );
};

export default TableContainer;